// src/app/core/services/update/app-update.service.ts (Modified for web testing)
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Platform } from '@ionic/angular';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UpdateDialogComponent } from '../../components/update-dialog/update-dialog.component';
import { ScreenService } from '../responsive/screen.service';
import { CapacitorHttp } from '@capacitor/core';
import pkg from 'package.json';

@Injectable({
  providedIn: 'root'
})
export class AppUpdateService {
  private readonly platform = inject(Platform);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);

  private readonly updateAvailable$ = new BehaviorSubject<boolean>(false);
  private readonly UPDATE_CHECK_INTERVAL = 30 * 60 * 1000; // 30 minutes
  private readonly screenService = inject(ScreenService);
  private isDialogOpen = false;

  constructor() {
    this.startPeriodicUpdateChecks();
  }

  get updateAvailable(): Observable<boolean> {
    return this.updateAvailable$.asObservable();
  }

  async checkForUpdates(): Promise<void> {
    try {
      // Prevent update check on statements page (e.g. returning from Netcash)
      if (this.router.url.includes('/paymnets/statements') || this.router.url.includes('payments/statements') || this.router.url.includes('payments')) {
        console.log('Skipping update check on statements page');
        return;
      }
      // Only check for updates on actual mobile devices, not in browsers
      if (this.platform.is('mobileweb') || this.platform.is('desktop')) {
        return;
      }

      if (this.platform.is('android') && this.platform.is('capacitor')) {
        await this.checkAndroidUpdates();
      } else if (this.platform.is('ios') && this.platform.is('capacitor')) {
        await this.checkiOSUpdates();
      }
    } catch (error) {
      console.warn('Failed to check for updates:', error);
    }
  }

  private async checkMockUpdates(): Promise<void> {
    // Simulate finding an update
    const shouldShowUpdate = Math.random() > 0.7; // 30% chance for demo

    if (shouldShowUpdate) {
      this.updateAvailable$.next(true);
      const isRequired = Math.random() > 0.6; // 40% chance it's required
      await this.showUpdateDialog(isRequired, 'mock');
    }
  }

  private async checkAndroidUpdates(): Promise<void> {

    try {
      const updateInfo = await AppUpdate.getAppUpdateInfo();

      if (updateInfo.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE) {
        this.updateAvailable$.next(true);

        if (updateInfo.immediateUpdateAllowed) {
          await this.showUpdateDialog(true, 'immediate');
        } else if (updateInfo.flexibleUpdateAllowed) {
          await this.showUpdateDialog(false, 'flexible');
        }
      }
    } catch (error) {
      console.error('❌ Android update check error:', error);

      // Handle the specific "app not owned" error
      if (error instanceof Error && error.message?.includes('ERROR_APP_NOT_OWNED')) {
        await this.handleSideloadedApp();
      } else {
        // For other errors, show manual update option
        await this.showManualUpdateOption();
      }
    }
  }

  private async checkiOSUpdates(): Promise<void> {
    try {
      // Use iTunes Search API to check version
      const response = await CapacitorHttp.get({
        url: `https://itunes.apple.com/za/lookup?bundleId=za.co.curro.curromyhub`
      });

      // Parse the data if it comes as a string
      let data = response.data;
      if (typeof data === 'string') {
        data = JSON.parse(data);
      }

      if (data && data.results && data.results.length > 0) {
        const storeVersion = data.results[0].version;
        const currentVersion = pkg.version;

        if (this.isNewerVersion(storeVersion, currentVersion)) {
          this.updateAvailable$.next(true);
          await this.showUpdateDialog(false, 'flexible');
        }
      }
    } catch (error) {
      console.error('iOS update check error:', error);
    }
  }

  private isNewerVersion(storeVersion: string, currentVersion: string): boolean {
    // Simple version comparison (you might want a more robust implementation)
    const normalize = (v: string) => v.split('.').map(n => parseInt(n, 10));
    const storeV = normalize(storeVersion);
    const currentV = normalize(currentVersion);

    for (let i = 0; i < Math.max(storeV.length, currentV.length); i++) {
      const s = storeV[i] || 0;
      const c = currentV[i] || 0;
      if (s > c) return true;
      if (s < c) return false;
    }
    return false;
  }

  private async handleSideloadedApp(): Promise<void> {
    // Prevent duplicate dialogs
    if (this.isDialogOpen) {
      return;
    }

    // For both development and production, show update dialog with custom message
    if (!environment.production) {
      await this.showUpdateDialog(false, 'mock');
    } else {
      await this.showUpdateDialog(false, 'sideloaded');
    }
  }

  private async showManualUpdateOption(): Promise<void> {
    const shouldCheck = confirm(
      'Unable to check for updates automatically.\n\nWould you like to check for updates manually in the app store?'
    );

    if (shouldCheck) {
      await this.updateNow();
    }
  }

  private async openPlayStore(): Promise<void> {
    try {
      const currentApp = await App.getInfo();
      await Browser.open({
        url: `https://play.google.com/store/apps/details?id=${currentApp.id}`,
        windowName: '_system'
      });
    } catch (error) {
      console.error('Failed to open Play Store:', error);
    }
  }

  private async showUpdateDialog(isRequired: boolean, updateType: 'immediate' | 'flexible' | 'mock' | 'sideloaded'): Promise<void> {
    // Prevent duplicate dialogs
    if (this.isDialogOpen) {
      return;
    }

    this.isDialogOpen = true;
    const isMobile = this.screenService.isMobile();

    // Custom message for sideloaded apps
    const customMessage = updateType === 'sideloaded'
      ? 'To receive automatic updates, please download this app from the Google Play Store.'
      : undefined;

    const dialogRef = this.dialog.open(UpdateDialogComponent, {
      data: {
        isRequired,
        updateType,
        platform: this.platform.is('ios') ? 'ios' : 'android',
        customMessage
      },
      disableClose: isRequired,
      width: isMobile ? '100dvw' : '90%',
      height: isMobile ? '100dvh' : 'auto',
      maxWidth: isMobile ? '100dvw' : '400px',
      maxHeight: isMobile ? '100dvh' : '90vh',
      panelClass: isMobile ? ['update-dialog-panel', 'full-screen-mobile'] : 'update-dialog-panel',
      hasBackdrop: true,
      backdropClass: 'update-dialog-backdrop'
    });

    // Reset the flag when dialog closes
    dialogRef.afterClosed().subscribe(() => {
      this.isDialogOpen = false;
    });
  }

  async updateNow(): Promise<void> {
    try {
      // Launch to the appropriate store
      if (this.platform.is('ios')) {
        await Browser.open({
          url: `https://apps.apple.com/za/app/curro-myhub-2/id6754881300`,
          windowName: '_system'
        });
      } else if (this.platform.is('android')) {
        await AppUpdate.openAppStore();
      }
    } catch (error) {
      console.error('Failed to update:', error);
    }
  }

  private startPeriodicUpdateChecks(): void {
    setTimeout(() => {
      this.checkForUpdates();
    }, 5000);

    // Only set interval for real devices
    if (!this.platform.is('mobileweb') && !this.platform.is('desktop')) {
      setInterval(() => {
        this.checkForUpdates();
      }, this.UPDATE_CHECK_INTERVAL);
    }
  }

  dismissUpdate(version: string): void {
    localStorage.setItem('dismissed-update-version', version);
    this.updateAvailable$.next(false);
  }

  // Add this to your AppUpdateService for testing
  async simulatePlayStoreUpdate(): Promise<void> {
    // Simulate different update scenarios
    const scenarios = [
      { type: 'flexible', required: false },
      // { type: 'immediate', required: true },
      // { type: 'mock', required: false }
    ];

    const randomScenario = scenarios[Math.floor(Math.random() * scenarios.length)];

    this.updateAvailable$.next(true);
    await this.showUpdateDialog(randomScenario.required, randomScenario.type as any);
  }
}
